import { type Component } from 'solid-js';
import { Panel } from '~ui/panel';
import { Toolbar, ToolbarSection } from '~ui/toolbar';
import { Heading, Text } from '~ui/text';
import { Table, type Column } from '~ui/table';
import { formatCurrency, perYear } from '~/lib/format';
import { drip, payDownDebt, projection, summary, type YearRow } from '~/stores/calc-store';
import { unpricedSymbols } from '~/stores/portfolio-store';

const COLUMNS: Column<YearRow>[] = [
    { id: 'year', header: 'Year', size: 0.5, cell: (r) => r().year },
    { id: 'value', header: 'Market value', align: 'right', cell: (r) => formatCurrency(r().value) },
    { id: 'dist', header: 'Distributions', align: 'right', cell: (r) => <Text tone='green'>{formatCurrency(r().distributions)}</Text> },
    { id: 'interest', header: 'Interest', align: 'right', size: 0.8, cell: (r) => <Text tone='red'>{formatCurrency(r().interest)}</Text> },
    { id: 'net', header: 'Net (pre-tax)', align: 'right', size: 1.1, cell: (r) => <Text tone={r().netIncome >= 0 ? 'green' : 'red'}>{formatCurrency(r().netIncome)}</Text> },
    { id: 'debt', header: 'Debt', align: 'right', size: 0.9, cell: (r) => formatCurrency(r().debt) },
    { id: 'equity', header: 'Equity', align: 'right', cell: (r) => formatCurrency(r().equity) },
];

const Projection: Component = () => (
    <Panel>
        <Toolbar>
            <ToolbarSection>
                <Heading level={2} class='pl-2'>
                    Projection
                </Heading>
            </ToolbarSection>
            <ToolbarSection>
                <Text tone='muted' class='whitespace-nowrap pr-2'>
                    {payDownDebt() ? 'Net income pays down loans' : drip() ? 'Distributions reinvested' : 'Distributions taken as cash'}
                </Text>
            </ToolbarSection>
        </Toolbar>

        <Table
            columns={COLUMNS}
            rows={projection()}
            // Distinguish "nothing to forecast" from "cannot forecast": a
            // projection missing some positions would look complete and be wrong.
            empty={unpricedSymbols().length > 0 ? `No price for ${unpricedSymbols().join(', ')} — set them in the instrument pool to project` : 'Set a year count to project'}
            footer={
                <div class='flex items-center justify-between gap-2 whitespace-nowrap'>
                    <Text>Cumulative net {formatCurrency(summary().cumulativeNet)}</Text>
                    <Text weight='medium'>
                        {formatCurrency(summary().finalValue)} value · {formatCurrency(summary().finalEquity)} equity
                    </Text>
                </div>
            }
        />
    </Panel>
);

export default Projection;
